import type { Rail, Vec2 } from "../level";
import { checkColor, type Local, type PieceKind, type Placed, type PrimKind } from "./types";

/**
 * A water slide's chute (#163): a U-shaped trough you ride down, following
 * a path of points with rounded bends, its floor falling steadily from `y`
 * at the first point to `yb` at the last. Across it there's a flat floor
 * and a wall each side that's a transition up to vert (`wall` its radius),
 * so it carves you round its bends like a bowl's walls do. Solid down to
 * the ground (the heightfield has no undersides), with nothing past its
 * walls. One piece compiles to a straight primitive per leg and a bend
 * primitive per corner, laid end to end so none overlap.
 *
 * Straight: ±hu along u, falling from `h0` at -u to `h1` at +u.
 * Bend: (x, z) is the corner's centre; it turns from local angle 0 (the +u
 * axis) through `sweep` radians (+ toward +w), `r` out, falling from `h0`
 * to `h1`. Its `hw` is the square that holds it; `tw` is the trough's.
 */
export interface ChutePrim extends Placed {
  kind: "chute";
  /** The trough's half width (the straight's `hw` too). */
  tw: number;
  /** The walls' transition radius (their height over the floor). */
  wall: number;
  h0: number;
  h1: number;
  /** A straight leg's half length; 0 on a bend. */
  hu: number;
  /** A bend's centreline radius and signed sweep (radians); left out on a straight. */
  bend?: { r: number; sweep: number };
  /** Render only: its own color. */
  color?: string;
}

export interface ChutePiece {
  kind: "chute";
  /** World points the trough's centreline runs through. */
  path: { x: number; z: number }[];
  /** Floor height at the first point and the last (m). */
  y: number;
  yb: number;
  /** The trough's half width. */
  hw: number;
  /** The walls' height, their transition radius (default `CHUTE_WALL`). */
  wall?: number;
  /** The bends' centreline radius (default `CHUTE_TURN`). */
  turn?: number;
  color?: string;
}

export const CHUTE_WALL = 1;
/** A straight leg runs along +u. */
const ALONG = { u: 1, w: 0 } as const;
export const CHUTE_TURN = 2.5;

/** The trough's height over its floor at `d` across from the centreline, its slope outward, and how far up the wall. */
function across(p: ChutePrim, d: number): { lift: number; g: number; up: number } {
  const up = Math.abs(d) - (p.tw - p.wall);
  if (up <= 0) return { lift: 0, g: 0, up };
  const dd = Math.min(up, p.wall - 1e-3);
  const root = Math.sqrt(p.wall * p.wall - dd * dd);
  return { lift: p.wall - root, g: dd / root, up };
}

export const CHUTE: PrimKind<ChutePrim> = {
  eval(p, u, w) {
    const b = p.bend;
    if (!b) {
      if (Math.abs(u) > p.hu || Math.abs(w) > p.tw) return null;
      const fu = (p.h1 - p.h0) / (2 * p.hu);
      const floor = p.h0 + fu * (u + p.hu);
      const a = across(p, w);
      if (a.up <= 0) return { h: floor, du: fu, dw: 0, guide: ALONG };
      const sw = w < 0 ? -1 : 1;
      return { h: floor + a.lift, du: fu, dw: a.g * sw, quarter: { u: a.up, r: p.wall, axis: { u: 0, w: sw } }, guide: ALONG };
    }
    const rho = Math.hypot(u, w);
    if (rho < 1e-6) return null;
    const t = Math.atan2(w, u) / b.sweep;
    if (!(t >= 0 && t <= 1)) return null;
    const d = rho - b.r;
    if (Math.abs(d) > p.tw) return null;
    const floor = p.h0 + (p.h1 - p.h0) * t;
    // The floor falls along the arc: its gradient is k / rho round it.
    const k = (p.h1 - p.h0) / b.sweep / (rho * rho);
    const fu = -w * k;
    const fw = u * k;
    // Round the bend, the way the arc runs (+sweep turns from +u toward +w).
    const sg = b.sweep < 0 ? -1 : 1;
    const guide = { u: (-w * sg) / rho, w: (u * sg) / rho };
    const a = across(p, d);
    if (a.up <= 0) return { h: floor, du: fu, dw: fw, guide };
    const sr = d < 0 ? -1 : 1;
    const ou = (sr * u) / rho;
    const ow = (sr * w) / rho;
    const l: Local = { h: floor + a.lift, du: fu + a.g * ou, dw: fw + a.g * ow, quarter: { u: a.up, r: p.wall, axis: { u: ou, w: ow }, curved: true }, guide };
    return l;
  },
  extent: (p) => (p.bend ? [-p.hw, p.hw] : [-p.hu, p.hu]),
  shadow: 0.55,
  top: (p) => Math.max(p.h0, p.h1) + p.wall,
  plan: (p) => (p.bend ? [] : [{ u0: -p.hu, u1: p.hu, fill: "ramp" }]),
};

/** One leg or bend of a chute, in world terms, before it's a primitive. */
interface Part {
  len: number;
  make(h0: number, h1: number): ChutePrim;
}

/** The chute's pieces along its path: legs between the bends' tangent points, and the bends. */
export function chuteParts(p: ChutePiece): ChutePrim[] {
  const tw = p.hw;
  const wall = p.wall ?? CHUTE_WALL;
  const R = p.turn ?? CHUTE_TURN;
  const pts = p.path;
  const dirs: Vec2[] = [];
  for (let i = 1; i < pts.length; i++) {
    const dx = pts[i]!.x - pts[i - 1]!.x;
    const dz = pts[i]!.z - pts[i - 1]!.z;
    const l = Math.hypot(dx, dz);
    dirs.push({ x: dx / l, z: dz / l });
  }
  // Each corner's turn (signed, + toward the left of the way in) and how far back along each leg its bend starts.
  const turn = (i: number): number => {
    const a = dirs[i - 1]!;
    const b = dirs[i]!;
    return Math.atan2(a.x * b.z - a.z * b.x, a.x * b.x + a.z * b.z);
  };
  const cut = (i: number): number => (i <= 0 || i >= pts.length - 1 ? 0 : R * Math.tan(Math.abs(turn(i)) / 2));
  const base = { kind: "chute" as const, tw, wall, ...(p.color === undefined ? {} : { color: p.color }) };
  const parts: Part[] = [];
  for (let i = 1; i < pts.length; i++) {
    const d = dirs[i - 1]!;
    const a = pts[i - 1]!;
    const b = pts[i]!;
    const s0 = cut(i - 1);
    const s1 = cut(i);
    const len = Math.hypot(b.x - a.x, b.z - a.z) - s0 - s1;
    const mid = s0 + len / 2;
    if (len > 1e-6) {
      parts.push({
        len,
        make: (h0, h1) => ({ ...base, x: a.x + d.x * mid, z: a.z + d.z * mid, rot: Math.atan2(d.z, d.x), hw: tw, hu: len / 2, h0, h1 }),
      });
    }
    if (i < pts.length - 1) {
      const phi = turn(i);
      const sg = phi < 0 ? -1 : 1;
      // The bend starts s1 back from the corner; its centre is R off to the side it turns.
      const sx = b.x - d.x * s1;
      const sz = b.z - d.z * s1;
      // Left of (dx, dz) in the x-z plane, the way +w is of +u.
      const cx = sx + -d.z * R * sg;
      const cz = sz + d.x * R * sg;
      const rot = Math.atan2(sz - cz, sx - cx);
      parts.push({
        len: R * Math.abs(phi),
        make: (h0, h1) => ({ ...base, x: cx, z: cz, rot, hw: R + tw, hu: 0, h0, h1, bend: { r: R, sweep: phi } }),
      });
    }
  }
  const total = parts.reduce((n, q) => n + q.len, 0);
  let run = 0;
  return parts.map((q) => {
    const h0 = p.y + ((p.yb - p.y) * run) / total;
    run += q.len;
    return q.make(h0, p.y + ((p.yb - p.y) * run) / total);
  });
}

/** How far round a bend (radians) each straight piece of its coping goes at most. */
const COPING_STEP = Math.PI / 16;

/**
 * The chute's wall tops (#187): a coping along each wall's lip, as drawn
 * (the trough's height at its edge), from the first point to the last.
 * Straight along a leg, short chords round a bend; all `joined`, so a
 * grind carries on round the bends instead of popping off at each seam.
 */
export function chuteCoping(p: ChutePiece): Rail[] {
  const rails: Rail[] = [];
  for (const part of chuteParts(p)) {
    const lift = across(part, part.tw).lift;
    const c = Math.cos(part.rot);
    const s = Math.sin(part.rot);
    const at = (u: number, w: number): Vec2 => ({ x: part.x + u * c - w * s, z: part.z + u * s + w * c });
    const b = part.bend;
    const n = b ? Math.max(2, Math.ceil(Math.abs(b.sweep) / COPING_STEP)) : 1;
    for (const side of [-1, 1]) {
      const d = side * part.tw;
      const pt = (k: number): Vec2 => {
        if (!b) return at(-part.hu + (2 * part.hu * k) / n, d);
        const a = (b.sweep * k) / n;
        return at((b.r + d) * Math.cos(a), (b.r + d) * Math.sin(a));
      };
      const y = (k: number) => part.h0 + ((part.h1 - part.h0) * k) / n + lift;
      for (let k = 0; k < n; k++) {
        const y0 = y(k);
        const y1 = y(k + 1);
        rails.push({ a: pt(k), b: pt(k + 1), y: y0, ...(y1 === y0 ? {} : { yb: y1 }), kind: "coping", joined: true });
      }
    }
  }
  return rails;
}

export const CHUTE_PIECE: PieceKind<ChutePiece> = {
  fields: ["path", "y", "yb", "hw", "wall", "turn", "color"],
  placed: false,
  check(c, o, path, bounds) {
    c.num(o, "y", path, { min: 0, max: 10 });
    c.num(o, "yb", path, { min: 0, max: 10 });
    const hw = c.num(o, "hw", path, { min: 0.6, max: 4 });
    const wall = c.num(o, "wall", path, { min: 0.3, max: 3, optional: true });
    const turn = c.num(o, "turn", path, { min: 1, max: 20, optional: true });
    checkColor(c, o, path);
    if (Number.isFinite(hw) && (wall ?? CHUTE_WALL) >= hw) c.fail(`${path}.wall`, "must be less than `hw`, to leave the trough a floor");
    if (Number.isFinite(hw) && (turn ?? CHUTE_TURN) <= hw) c.fail(`${path}.turn`, "must be more than `hw`");
    if (!Array.isArray(o.path) || o.path.length < 2) {
      c.fail(`${path}.path`, "expected a list of at least 2 points");
      return;
    }
    const pts: Vec2[] = [];
    o.path.forEach((v, i) => {
      const pt = c.obj(v, `${path}.path[${i}]`);
      if (!pt) return;
      c.only(pt, ["x", "z"], `${path}.path[${i}]`);
      const x = c.num(pt, "x", `${path}.path[${i}]`, { min: -bounds, max: bounds });
      const z = c.num(pt, "z", `${path}.path[${i}]`, { min: -bounds, max: bounds });
      pts.push({ x, z });
    });
    if (pts.length !== o.path.length || !pts.every((q) => Number.isFinite(q.x) && Number.isFinite(q.z))) return;
    const R = turn ?? CHUTE_TURN;
    const cuts = pts.map((q, i) => {
      if (i === 0 || i === pts.length - 1) return 0;
      const a = pts[i - 1]!;
      const b = pts[i + 1]!;
      const t0 = Math.atan2(q.z - a.z, q.x - a.x);
      const t1 = Math.atan2(b.z - q.z, b.x - q.x);
      const phi = Math.abs(Math.atan2(Math.sin(t1 - t0), Math.cos(t1 - t0)));
      if (phi > (2 * Math.PI) / 3) c.fail(`${path}.path[${i}]`, "turns more than 120 degrees");
      return R * Math.tan(Math.min(phi, (2 * Math.PI) / 3) / 2);
    });
    for (let i = 1; i < pts.length; i++) {
      const l = Math.hypot(pts[i]!.x - pts[i - 1]!.x, pts[i]!.z - pts[i - 1]!.z);
      if (l < 1e-6) c.fail(`${path}.path[${i}]`, "is on top of the point before it");
      else if (cuts[i - 1]! + cuts[i]! > l + 1e-6) c.fail(`${path}.path[${i}]`, "too close to the point before it for the bends at both ends (`turn`)");
    }
  },
  compile(p, out) {
    out.prims.push(...chuteParts(p));
    out.rails.push(...chuteCoping(p));
  },
};
